import React, { useState } from 'react';
import { Container, Row, Col, Card, Button, Alert, Modal, Form, ProgressBar } from 'react-bootstrap';
import {
  CheckCircleFill,
  PencilSquare,
  CloudUpload,
  CheckCircle,
  ExclamationTriangle,
  LockFill,
  EyeFill,
  EyeSlashFill,
  ShieldCheck,
  ClockFill,
  XCircleFill,
  FileEarmarkText,
  Trash3,
  PersonFill,
  Bank2,
} from 'react-bootstrap-icons';
import NgoLayout from '../../../../layouts/Ngo/NgoLayout';
import '../../../../assets/css/Ngo/controlpanelCSS/ngoProfile.css';

/* ─────────────────────────────────────────
   HELPER
───────────────────────────────────────── */
const getOverallStatus = (docs) => {
  if (docs.every((d) => d.status === 'approved')) return 'verified';
  if (docs.some((d) => d.status === 'rejected'))  return 'rejected';
  return 'pending';
};

/* ─────────────────────────────────────────
   SUB-COMPONENT: Single Upload Input
───────────────────────────────────────── */
const SingleUploadInput = ({ onFileSelect, onFileRemove }) => {
  const [stagedFile, setStagedFile] = useState(null);

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setStagedFile(file);
    onFileSelect(file);
  };

  const handleRemove = () => {
    setStagedFile(null);
    onFileRemove();
  };

  return (
    <div className="npv-single-upload-card">
      <p className="npv-single-upload-title">
        <CloudUpload size={16} className="me-2" />
        Upload All Documents (Single File)
      </p>
      <p className="npv-single-upload-sub">
        Combine all required documents into one PDF and upload below.
      </p>

      {stagedFile ? (
        <div className="npv-file-chosen">
          <span className="npv-file-name">📄 {stagedFile.name}</span>
          <button className="npv-file-remove" onClick={handleRemove}>
            <Trash3 size={13} /> Remove
          </button>
        </div>
      ) : (
        <label className="npv-upload-label npv-upload-label-lg">
          <CloudUpload size={22} />
          <span>Click to choose file</span>
          <input
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            className="npv-file-input"
            onChange={handleFile}
          />
        </label>
      )}
      <span className="npv-file-hint mt-2 d-block">PDF, JPG or PNG — max 5MB</span>
    </div>
  );
};

/* ─────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────── */
const NGOProfile = ({ isAdmin = false }) => {

  const [activeTab, setActiveTab] = useState('basic');

  const [profileData, setProfileData] = useState({
    name:               'Education Foundation Pakistan',
    registrationNumber: 'REG-2020-PKR-001234',
    type:               'Education / Health / Relief',
    yearEstablished:    '2015',
    location:           'Islamabad, Pakistan',
    email:              'info@ngo.org',
    phone:              '+923001234567',
    website:            'www.educationfoundation.org',
  });
  const [editDraft, setEditDraft] = useState({ ...profileData });

  const [bankData, setBankData] = useState({
    bankName:     'Allied Bank Limited',
    accountTitle: 'Education Foundation Pakistan',
    iban:         'PK36ABPA0010001234567890',
    branchCode:   '0100',
  });
  const [bankDraft, setBankDraft] = useState({ ...bankData });

  const [documents, setDocuments] = useState([
    { id: 1, name: 'Registration Certificate', status: 'approved', date: '2024-01-15', file: null, reason: '' },
    { id: 2, name: 'NTN / Tax Certificate',    status: 'pending',  date: '2024-01-20', file: null, reason: '' },
    { id: 3, name: 'Bank Account Proof',       status: 'pending',  date: '2024-01-18', file: null, reason: '' },
    { id: 4, name: 'Authorized Person CNIC',   status: 'rejected', date: '2024-01-10', file: null, reason: 'Document not clear' },
    { id: 5, name: 'Office Address Proof',     status: 'approved', date: '2024-01-12', file: null, reason: '' },
    { id: 6, name: 'NGO Annual Report',        status: 'pending',  date: '2024-01-22', file: null, reason: '' },
    { id: 7, name: 'Board Resolution Letter',  status: 'pending',  date: '2024-01-23', file: null, reason: '' },
  ]);

  const [showEditModal,     setShowEditModal]     = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [editSection,       setEditSection]       = useState('profile');

  const [passwordForm, setPasswordForm] = useState({ current: '', newPass: '', confirm: '' });
  const [showCurrent,  setShowCurrent]  = useState(false);
  const [showNew,      setShowNew]      = useState(false);
  const [showConfirm,  setShowConfirm]  = useState(false);
  const [pwError,      setPwError]      = useState('');
  const [pwSuccess,    setPwSuccess]    = useState(false);
  const [saveSuccess,  setSaveSuccess]  = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);

  /* ── Derived ── */
  const overallStatus = getOverallStatus(documents);
  const approvedCount = documents.filter((d) => d.status === 'approved').length;
  const rejectedCount = documents.filter((d) => d.status === 'rejected').length;
  const progressValue = Math.round((approvedCount / documents.length) * 100);

  /* ── Profile handlers ── */
  const openEditProfile = () => { setEditDraft({ ...profileData }); setEditSection('profile'); setShowEditModal(true); };
  const openEditBank    = () => { setBankDraft({ ...bankData });    setEditSection('bank');    setShowEditModal(true); };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    if (editSection === 'profile') setEditDraft((p) => ({ ...p, [name]: value }));
    else                           setBankDraft((p) => ({ ...p, [name]: value }));
  };

  const handleEditSave = () => {
    if (editSection === 'profile') setProfileData({ ...editDraft });
    else                           setBankData({ ...bankDraft });
    setShowEditModal(false);
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 3000);
  };

  /* ── Password handlers ── */
  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm((p) => ({ ...p, [name]: value }));
    setPwError('');
  };

  const handlePasswordSave = () => {
    if (!passwordForm.current)                         { setPwError('Please enter your current password.'); return; }
    if (passwordForm.newPass.length < 8)               { setPwError('New password must be at least 8 characters.'); return; }
    if (passwordForm.newPass !== passwordForm.confirm) { setPwError('New passwords do not match.'); return; }
    setPwSuccess(true);
    setPasswordForm({ current: '', newPass: '', confirm: '' });
    setTimeout(() => { setPwSuccess(false); setShowPasswordModal(false); }, 1800);
  };

  /* ── Document handlers ── */
  const handleFileSelect = (docId, file) => {
    setDocuments((prev) =>
      prev.map((d) =>
        d.id === docId
          ? { ...d, file, status: 'pending', date: new Date().toISOString().split('T')[0] }
          : d
      )
    );
  };

  const handleFileRemove = (docId) => {
    setDocuments((prev) => prev.map((d) => (d.id === docId ? { ...d, file: null } : d)));
  };

  /* ── Inline render helpers ── */
  const StatusBadge = ({ status }) => {
    const map = {
      approved: { cls: 'npv-badge-approved', icon: <CheckCircleFill />, label: 'Approved' },
      pending:  { cls: 'npv-badge-pending',  icon: <ClockFill />,        label: 'Pending'  },
      rejected: { cls: 'npv-badge-rejected', icon: <XCircleFill />,      label: 'Rejected' },
    };
    const c = map[status] || map.pending;
    return <span className={`npv-status-chip ${c.cls}`}>{c.icon} {c.label}</span>;
  };

  const OverallBanner = () => {
    if (overallStatus === 'verified') return (
      <Alert className="npv-banner npv-banner-verified">
        <div className="npv-banner-left"><ShieldCheck size={26} /><span>Your NGO is <strong>Verified</strong> — all documents approved.</span></div>
        <span className="npv-overall-chip npv-chip-verified"><CheckCircle /> Verified NGO</span>
      </Alert>
    );
    if (overallStatus === 'rejected') return (
      <Alert className="npv-banner npv-banner-rejected">
        <div className="npv-banner-left"><XCircleFill size={26} /><span>Some documents were <strong>Rejected</strong>. Please re-upload the flagged documents.</span></div>
        <span className="npv-overall-chip npv-chip-rejected"><XCircleFill /> Action Required</span>
      </Alert>
    );
    return (
      <Alert className="npv-banner npv-banner-pending">
        <div className="npv-banner-left"><ClockFill size={26} /><span>Verification is <strong>Pending</strong> — our team is reviewing your documents.</span></div>
        <span className="npv-overall-chip npv-chip-pending"><ClockFill /> Under Review</span>
      </Alert>
    );
  };

  /* ── Tab definitions ── */
  const TABS = [
    { key: 'basic', label: 'Basic Information', icon: <PersonFill size={14} /> },
    { key: 'bank',  label: 'Bank Details',       icon: <Bank2 size={14} /> },
    {
      key:   'kyc',
      label: 'KYC Verification',
      icon:  <ShieldCheck size={14} />,
      badge: rejectedCount > 0 ? rejectedCount : null,
    },
  ];

  /* ══════════════════════════════ JSX ══════════════════════════════ */
  return (
    <NgoLayout>
      <div className="npv-container">
        <Container className="npv-wrapper">

          {/* Page Header */}
          <div className="npv-page-header">
            <h1 className="npv-page-title">NGO Profile & Verification</h1>
            <div className="npv-header-actions">
              <Button className="npv-btn-password" onClick={() => setShowPasswordModal(true)}>
                <LockFill className="me-2" size={14} /> Reset Password
              </Button>
            </div>
          </div>

          {saveSuccess && (
            <Alert className="npv-toast-success">
              <CheckCircleFill className="me-2" /> Profile updated successfully!
            </Alert>
          )}

          <Row className="justify-content-center">
            <Col lg={10} xl={8}>

              <OverallBanner />

              {/* Verification Progress */}
              <div className="npv-progress-wrap">
                <div className="npv-progress-header">
                  <span>Verification Progress</span>
                  <span>{approvedCount} / {documents.length} documents approved</span>
                </div>
                <ProgressBar now={progressValue} className="npv-progress-bar" />
              </div>

              {/* Tabs outer wrapper */}
              <div className="npv-tabs-outer mt-4">

                {/* Tab Nav */}
                <div className="npv-tabs-nav">
                  {TABS.map((tab) => (
                    <button
                      key={tab.key}
                      className={`npv-tab-btn ${activeTab === tab.key ? 'npv-tab-active' : ''}`}
                      onClick={() => setActiveTab(tab.key)}
                    >
                      <span className="npv-tab-icon">{tab.icon}</span>
                      <span className="npv-tab-label">{tab.label}</span>
                      {tab.badge && <span className="npv-tab-badge">{tab.badge}</span>}
                      {tab.key === 'kyc' && !tab.badge && (
                        <span className={`npv-tab-dot npv-tab-dot-${overallStatus}`} />
                      )}
                    </button>
                  ))}
                </div>

                {/* ───────── BASIC INFORMATION ───────── */}
                {activeTab === 'basic' && (
                  <Card className="npv-card npv-tab-card">
                    <Card.Header className="npv-card-header">
                      <h2 className="npv-section-title">Basic Information</h2>
                      <Button className="npv-btn-edit" onClick={openEditProfile}>
                        <PencilSquare className="me-2" size={15} /> Edit Profile
                      </Button>
                    </Card.Header>
                    <Card.Body className="npv-card-body">
                      <Row>
                        <Col md={3} className="text-center mb-4 mb-md-0">
                          <div className="npv-logo-box">
                            <div className="npv-logo-text"><div>NGO</div><div>LOGO</div></div>
                          </div>
                        </Col>
                        <Col md={9}>
                          <Row className="g-4">
                            {[
                              ['NGO Name',            'name'],
                              ['Registration Number', 'registrationNumber'],
                              ['NGO Type',            'type'],
                              ['Year Established',    'yearEstablished'],
                              ['Country / City',      'location'],
                              ['Official Email',      'email'],
                              ['Phone Number',        'phone'],
                              ['Website',             'website'],
                            ].map(([label, key]) => (
                              <Col md={6} key={key}>
                                <div className="npv-field">
                                  <label className="npv-label">{label}</label>
                                  <p className="npv-value">{profileData[key]}</p>
                                </div>
                              </Col>
                            ))}
                          </Row>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                )}

                {/* ───────── BANK DETAILS ───────── */}
                {activeTab === 'bank' && (
                  <Card className="npv-card npv-tab-card">
                    <Card.Header className="npv-card-header">
                      <h2 className="npv-section-title">Bank & Payout Details</h2>
                      <Button className="npv-btn-edit" onClick={openEditBank}>
                        <PencilSquare className="me-2" size={15} /> Edit Bank Info
                      </Button>
                    </Card.Header>
                    <Card.Body className="npv-card-body">
                      <Alert className="npv-warning-alert mb-4">
                        <ExclamationTriangle className="me-2" />
                        Donations will be deposited to this account (Verification <strong>Required</strong>)
                      </Alert>
                      <Row className="g-4">
                        {[
                          ['Bank Name',             'bankName'],
                          ['Account Title',         'accountTitle'],
                          ['IBAN / Account Number', 'iban'],
                          ['Branch Code',           'branchCode'],
                        ].map(([label, key]) => (
                          <Col md={6} key={key}>
                            <div className="npv-field">
                              <label className="npv-label">{label}</label>
                              <p className="npv-value">{bankData[key]}</p>
                            </div>
                          </Col>
                        ))}
                      </Row>
                    </Card.Body>
                  </Card>
                )}

                {/* ───────── KYC VERIFICATION ───────── */}
                {activeTab === 'kyc' && (
                  <Card className="npv-card npv-tab-card">
                    <Card.Header className="npv-card-header">
                      <h2 className="npv-section-title">KYC Document Verification</h2>
                    </Card.Header>
                    <Card.Body className="npv-card-body">

                      <p className="npv-kyc-intro">
                        Upload clear, legible copies of the following documents. All documents must be valid and not expired.
                      </p>

                      {/* Required documents list — names on top */}
                      <div className="npv-required-docs-list mb-4">
                        <p className="npv-required-docs-heading">
                          <FileEarmarkText size={15} className="me-2" />
                          Required Documents
                        </p>
                        <ol className="npv-doc-names-ol">
                          {documents.map((doc) => (
                            <li key={doc.id} className="npv-doc-names-item">
                              <span className={`npv-doc-names-dot npv-dot-${doc.status}`} />
                              {doc.name}
                              <StatusBadge status={doc.status} />
                            </li>
                          ))}
                        </ol>
                      </div>

                      {/* Progress bar */}
                      <div className="npv-kyc-progress-wrap mb-4">
                        <div className="npv-progress-header">
                          <span>{approvedCount} of {documents.length} approved</span>
                          <span>{progressValue}%</span>
                        </div>
                        <ProgressBar now={progressValue} className="npv-progress-bar" />
                      </div>

                      {/* Single upload widget */}
                      <SingleUploadInput
                        onFileSelect={(file) => setUploadedFile(file)}
                        onFileRemove={() => setUploadedFile(null)}
                      />

                      {/* Rejected notices */}
                      {documents
                        .filter((d) => d.status === 'rejected')
                        .map((doc) => (
                          <div key={doc.id} className="npv-rejected-notice mt-3">
                            <XCircleFill size={13} className="me-2 flex-shrink-0" />
                            <span><strong>{doc.name}</strong>: {doc.reason} — please re-upload.</span>
                          </div>
                        ))
                      }

                      <div className="npv-kyc-submit-row mt-4">
                        <Button className="npv-btn-save">
                          <CloudUpload className="me-2" size={14} /> Submit for Review
                        </Button>
                      </div>

                    </Card.Body>
                  </Card>
                )}

              </div>{/* end npv-tabs-outer */}
            </Col>
          </Row>
        </Container>
      </div>

      {/* ════ MODAL: Edit Profile / Bank ════ */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered size="lg" className="npv-modal">
        <Modal.Header closeButton className="npv-modal-header">
          <Modal.Title className="npv-modal-title">
            <PencilSquare className="me-2" />
            {editSection === 'profile' ? 'Edit Basic Information' : 'Edit Bank Details'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="npv-modal-body">
          {editSection === 'profile' ? (
            <Row className="g-3">
              {[
                ['NGO Name',            'name',               'text'],
                ['Registration Number', 'registrationNumber', 'text'],
                ['NGO Type',            'type',               'text'],
                ['Year Established',    'yearEstablished',    'number'],
                ['Country / City',      'location',           'text'],
                ['Official Email',      'email',              'email'],
                ['Phone Number',        'phone',              'tel'],
                ['Website',             'website',            'url'],
              ].map(([label, key, type]) => (
                <Col md={6} key={key}>
                  <Form.Group>
                    <Form.Label className="npv-form-label">{label}</Form.Label>
                    <Form.Control className="npv-form-input" type={type} name={key} value={editDraft[key]} onChange={handleEditChange} />
                  </Form.Group>
                </Col>
              ))}
            </Row>
          ) : (
            <Row className="g-3">
              {[
                ['Bank Name',             'bankName',     'text'],
                ['Account Title',         'accountTitle', 'text'],
                ['IBAN / Account Number', 'iban',         'text'],
                ['Branch Code',           'branchCode',   'text'],
              ].map(([label, key, type]) => (
                <Col md={6} key={key}>
                  <Form.Group>
                    <Form.Label className="npv-form-label">{label}</Form.Label>
                    <Form.Control className="npv-form-input" type={type} name={key} value={bankDraft[key]} onChange={handleEditChange} />
                  </Form.Group>
                </Col>
              ))}
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer className="npv-modal-footer">
          <Button className="npv-btn-cancel" onClick={() => setShowEditModal(false)}>Cancel</Button>
          <Button className="npv-btn-save"   onClick={handleEditSave}>Save Changes</Button>
        </Modal.Footer>
      </Modal>

      {/* ════ MODAL: Reset Password ════ */}
      <Modal
        show={showPasswordModal}
        onHide={() => { setShowPasswordModal(false); setPwError(''); setPwSuccess(false); }}
        centered
        className="npv-modal"
      >
        <Modal.Header closeButton className="npv-modal-header">
          <Modal.Title className="npv-modal-title"><LockFill className="me-2" /> Reset Password</Modal.Title>
        </Modal.Header>
        <Modal.Body className="npv-modal-body">
          {pwSuccess && (
            <Alert className="npv-toast-success mb-3">
              <CheckCircleFill className="me-2" /> Password updated successfully!
            </Alert>
          )}
          {pwError && <Alert variant="danger" className="npv-pw-error">{pwError}</Alert>}
          {[
            ['Current Password', 'current', showCurrent, setShowCurrent],
            ['New Password',     'newPass', showNew,     setShowNew],
            ['Confirm Password', 'confirm', showConfirm, setShowConfirm],
          ].map(([label, name, show, setShow]) => (
            <Form.Group className="mb-3" key={name}>
              <Form.Label className="npv-form-label">{label}</Form.Label>
              <div className="npv-pw-wrap">
                <Form.Control
                  className="npv-form-input npv-pw-input"
                  type={show ? 'text' : 'password'}
                  name={name}
                  value={passwordForm[name]}
                  onChange={handlePasswordChange}
                  placeholder={`Enter ${label.toLowerCase()}`}
                />
                <button type="button" className="npv-pw-eye" onClick={() => setShow(!show)}>
                  {show ? <EyeSlashFill /> : <EyeFill />}
                </button>
              </div>
              {name === 'newPass' && (
                <Form.Text className="npv-pw-hint">Minimum 8 characters required.</Form.Text>
              )}
            </Form.Group>
          ))}
        </Modal.Body>
        <Modal.Footer className="npv-modal-footer">
          <Button className="npv-btn-cancel" onClick={() => setShowPasswordModal(false)}>Cancel</Button>
          <Button className="npv-btn-save"   onClick={handlePasswordSave}>Update Password</Button>
        </Modal.Footer>
      </Modal>

    </NgoLayout>
  );
};

export default NGOProfile;